import { Component, OnDestroy } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Subscription, catchError, tap } from 'rxjs';

import { FiltroTabelasAdministrativas } from '@tabelasAdministrativas/models/filtro';
import { TabelasAdministrativas } from '@core/models/tabelasAdministrativas';
import { UiService } from '@core/services/ui.service';
import { VinculoApiService } from '../services/vinculo-api.service';

@Component({
	templateUrl: './tabela-vinculo.component.html',
	providers: [MessageService, ConfirmationService]
})
export class TabelaVinculoComponent implements OnDestroy {
	pesquisarAoCarregar = true;
	habilitarFiltroCadastradoUsuario = false;
	filtros?: FiltroTabelasAdministrativas;
	limparFormularioPesquisa = false;
	resultado: any[] = [];
	loadingResultado = false;
	totalResultado?: number;
	pagina = 1;
	itensPorPagina = 10;
	primeiroItemResultado = 0;
	colunas: any;
	itemTabela = new TabelasAdministrativas();
	operacao?: string;
	exibirModalManterTabela = false;
	tituloModalManterTabela = '';
	subscriptions: Subscription[] = [];
	registrosSelecionados: TabelasAdministrativas[] = [];

	constructor(
		private confirmationService: ConfirmationService,
		private tabelaService: VinculoApiService,
		private messageService: MessageService,
		private ui: UiService
	) {}

	ngOnDestroy(): void {
		this.subscriptions.forEach(subscription => subscription.unsubscribe());
	}

	prepararCadastrar() {
		this.tituloModalManterTabela = 'Cadastrar tipo de vínculo';
		this.itemTabela = new TabelasAdministrativas();
		this.operacao = 'cadastrar';
		this.exibirModalManterTabela = true;
	}

	prepararAlterar(event: any) {
		this.subscriptions.push(
			this.tabelaService.obterTipoVinculo(event.id).subscribe(res => {
				this.itemTabela = new TabelasAdministrativas(res.id, res.nome, res.ativo);
				this.tituloModalManterTabela = 'Alterar tipo vínculo';
				this.operacao = 'alterar';
				this.exibirModalManterTabela = true;
			})
		);
	}

	ativarInativarEmLote(ativar = true) {
		this.messageService.clear();
		if (this.registrosSelecionados.length === 0)
			return;

		const listaIdRegistrosSelecionados: number[] = [];
		for (const item of this.registrosSelecionados) {
			listaIdRegistrosSelecionados.push(item.id!);
		}
		const operacao = ativar ? 'ativado(s)' : 'inativado(s)';
		this.subscriptions.push(
			this.tabelaService
				.ativarInativarTipoVinculo(listaIdRegistrosSelecionados!, ativar)
				.pipe(
					tap(() => {
						this.criarMensagem('success', `Tipo(s) de vínculo ${operacao} com sucesso`);
						this.pesquisar();
					}),
					catchError(err => {
						this.criarMensagem('error', err.error.message);
						return [];
					})
				)
				.subscribe()
		);
	}

	salvarItem(event: TabelasAdministrativas) {
		const vinculo = new TabelasAdministrativas(event.id, event.nome, event.ativo);
		vinculo.id = event.id;
		vinculo.nome = event.nome;
		vinculo.ativo = event.ativo ? true : false;
		if (this.operacao === 'cadastrar') {
			this.salvarNovo(vinculo);
		} else if (vinculo.id) {
			this.alterar(vinculo.id, vinculo);
		}
		this.operacao = '';
		this.exibirModalManterTabela = false;
	}

	executarPesquisar(event: any) {
		this.filtros = new FiltroTabelasAdministrativas(undefined, event.nome, event.ativo);
		this.primeiroItemResultado = 0;
		this.pagina = 1;
		this.messageService.clear();
		this.pesquisar();
		this.criarColunas();
	}

	limparFormulario() {
		if (this.pesquisarAoCarregar) {
			this.executarPesquisar(new FiltroTabelasAdministrativas());
			this.limparFormularioPesquisa = true;
		} else {
			this.resultado = [];
		}
	}

	excluirItem(event: any) {
		const vinculo = event;
		this.confirmationService.confirm({
			message: 'Esta ação não poderá ser desfeita.',
			header: 'Deseja excluir o vínculo ' + vinculo.nome + '?',
			accept: async () => {
				this.tabelaService.excluirTipoVinculo(event.id).subscribe(
					async (res: any) => {
						this.exibirMensagemSucesso(res);
					},
					err => {
						this.criarMensagem('error', err.error.message);
					}
				);
			}
		});
	}

	fecharModal() {
		this.exibirModalManterTabela = false;
		this.itemTabela = new TabelasAdministrativas();
		this.operacao = 'cadastrar';
	}

	paginar(event: any) {
		this.pagina = event.first / event.rows + 1;
		this.primeiroItemResultado = event.first;
		this.itensPorPagina = event.rows;
		this.pesquisar();
	}

	getItensPorPagina(event: any) {
		this.itensPorPagina = event.rows;
	}

	private pesquisar() {
		this.resultado = [];
		this.loadingResultado = true;
		if (this.filtros) {
			this.subscriptions.push(
				this.tabelaService.obterTipoVinculos(this.filtros, this.pagina, this.itensPorPagina).subscribe(
					(resultado: any) => {
						this.loadingResultado = false;
						this.totalResultado = resultado.metadata.total;
						this.pagina = resultado.metadata.page;
						this.tratarDados(resultado.items);
					},
					err => {
						this.totalResultado = 0;
						this.resultado = [];
						this.criarMensagem('error', err.error.message);
					}
				)
			);
		}
	}

	private criarColunas() {
		this.colunas = [
			{ field: 'id', header: 'id' },
			{ field: 'nome', header: 'Tipo de vínculo' },
			{ field: 'ativo', header: 'Ativo' }
		];
	}

	private tratarDados(itens: any[]) {
		for (const item of itens) {
			const resultadoTratado = {
				id: item.id,
				nome: item.nome,
				ativo: item.ativo ? 'Sim' : 'Não'
			};
			this.resultado.push(resultadoTratado);
		}
	}

	private salvarNovo(vinculo: TabelasAdministrativas) {
		this.subscriptions.push(
			this.tabelaService.incluirTipoVinculo(vinculo).subscribe(
				(res: any) => {
					this.exibirMensagemSucesso(res);
				},
				err => {
					this.criarMensagem('error', err.error.message);
				}
			)
		);
	}

	private alterar(id: number, vinculo: TabelasAdministrativas) {
		this.subscriptions.push(
			this.tabelaService.alterarTipoVinculo(id, vinculo).subscribe(
				(res: any) => {
					this.exibirMensagemSucesso(res);
				},
				err => {
					this.criarMensagem('error', err.error.message);
				}
			)
		);
	}

	private criarMensagem(criticidade: string, mensagem: string) {
		this.messageService.clear();
		this.messageService.add(this.ui.criarMensagem(criticidade, '', mensagem));
	}

	private exibirMensagemSucesso(res: any) {
		if (res) {
			this.criarMensagem('success', res.message);
			this.limparPesquisa();
		}
	}

	private limparPesquisa() {
		this.limparFormularioPesquisa = true;
		this.primeiroItemResultado = 0;
		this.pagina = 1;
		this.pesquisar();
	}
}
